import React from 'react';
import styled, { keyframes, css } from 'styled-components';
import styles from '../../../utility/styles';
import moment from 'moment';

const pulse = keyframes`
    0%, 100% { opacity: 1; }
    50% { opacity: 0.35; }
`

const pulseAnimation = css`
    animation: ${pulse} 2.5s ease-in-out infinite;
`

const Wrapper = styled.div`
    font-family: ${styles.fonts.geist};
`

const GRID_TESLA = '6px 2fr 1.5fr 0.5fr 0.6fr 24px'
const GRID_NO_FSD = '6px 2fr 0.5fr 0.6fr 24px'
const GRID_MOBILE = '6px 1fr 0.5fr 0.6fr 24px'

const Row = styled.a`
    position: relative;
    display: grid;
    grid-template-columns: ${({ noFsd }) => noFsd ? GRID_NO_FSD : GRID_TESLA};
    align-items: center;
    padding: 0;
    min-height: 48px;
    text-decoration: none;
    cursor: pointer;
    gap: 0;
    border-bottom: 1px solid rgba(45, 54, 70, 0.25);
    overflow: hidden;
    transition: background-color 0.1s;
    &:hover {
        background-color: rgba(16, 171, 255, 0.04);
        text-decoration: none;
    }
    @media only screen and (max-width: 768px) {
        grid-template-columns: ${GRID_MOBILE};
    }
`

const FleetBar = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: ${({ pct }) => Math.max(pct, 0.5)}%;
    background: linear-gradient(90deg, rgba(16, 171, 255, 0.08) 0%, rgba(16, 171, 255, 0.02) 100%);
    pointer-events: none;
    z-index: 0;
`

const Accent = styled.div`
    width: 3px;
    height: 100%;
    border-radius: 0 2px 2px 0;
    background-color: ${({ status }) =>
        status === 'rolling_out' ? styles.colors.chargingGreen :
        status === 'phasing_out' ? '#FF766C' :
        'transparent'
    };
    ${({ status }) => status === 'rolling_out' && pulseAnimation}
`

const VersionCol = styled.div`
    display: flex;
    align-items: baseline;
    gap: 12px;
    padding: 0 16px;
    z-index: 1;
`

const VersionNum = styled.span`
    font-family: ${styles.fonts.geistMono};
    font-size: 15px;
    font-weight: 600;
    color: ${styles.colors.white};
`

const StatusTag = styled.span`
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.3px;
    color: ${({ status }) =>
        status === 'rolling_out' ? styles.colors.chargingGreen :
        status === 'phasing_out' ? '#FF766C' :
        'transparent'
    };
`

const FsdCell = styled.span`
    font-family: ${styles.fonts.geistMono};
    font-size: 13px;
    font-weight: 500;
    color: #c4b5fd;
    z-index: 1;
    @media only screen and (max-width: 768px) {
        display: none;
    }
`

const Age = styled.span`
    font-family: ${styles.fonts.geistMono};
    font-size: 12px;
    color: ${styles.colors.mediumGray};
    text-align: right;
    padding-right: 16px;
    z-index: 1;
`

const Pct = styled.span`
    font-family: ${styles.fonts.geistMono};
    font-size: 15px;
    font-weight: 700;
    color: ${styles.colors.white};
    text-align: right;
    z-index: 1;
`

const Arrow = styled.span`
    font-size: 18px;
    color: ${styles.colors.darkGray};
    text-align: center;
    z-index: 1;
    transition: color 0.1s, transform 0.1s;
    ${Row}:hover & {
        color: ${styles.colors.lightGray};
        transform: translateX(2px);
    }
`

const HRow = styled.div`
    display: grid;
    grid-template-columns: ${({ noFsd }) => noFsd ? GRID_NO_FSD : GRID_TESLA};
    align-items: center;
    padding: 10px 0;
    gap: 0;
    @media only screen and (max-width: 768px) {
        grid-template-columns: ${GRID_MOBILE};
    }
`

const HCell = styled.span`
    font-size: 10px;
    font-weight: 600;
    color: ${styles.colors.mediumGray};
    text-transform: uppercase;
    letter-spacing: 0.8px;
    padding: 0 16px;
`

const Empty = styled.div`
    font-family: ${styles.fonts.geist};
    font-size: 14px;
    color: ${styles.colors.lightGray};
    text-align: center;
    padding: 48px 16px;
`

function relativeAge(dateStr) {
    if (!dateStr) return '-'
    const days = moment().startOf('day').diff(moment(dateStr).startOf('day'), 'days')
    if (days === 0) return 'today'
    if (days === 1) return '1d'
    if (days < 30) return `${days}d`
    const months = Math.floor(days / 30)
    return `${months}mo`
}

function formatPct(pct) {
    if (pct === 0 || pct === '0' || pct === 0.0) return '<0.1%'
    return `${pct}%`
}

function statusLabel(status) {
    if (status === 'rolling_out') return 'Rolling out'
    if (status === 'phasing_out') return 'Phasing out'
    return ''
}

export default function LatestList({ data = [], make_friendly_name, make }) {
    if (!data.length) return <Empty>No recent versions for {make_friendly_name}</Empty>
    const isTesla = make === 'tesla'

    return (
        <Wrapper>
            <HRow noFsd={!isTesla}>
                <span />
                <HCell>Version</HCell>
                {isTesla && <HCell className='d-none d-md-block'>FSD</HCell>}
                <HCell style={{ textAlign: 'right', paddingRight: '16px' }}>Age</HCell>
                <HCell style={{ textAlign: 'right' }}>Fleet</HCell>
                <span />
            </HRow>
            {data.map(({ version, first_detected_at, percent_of_fleet, fsd_versions, rollout_status, href }) => (
                <Row key={version} href={href} noFsd={!isTesla}>
                    <FleetBar pct={percent_of_fleet} />
                    <Accent status={isTesla ? rollout_status : null} />
                    <VersionCol>
                        <VersionNum>{version}</VersionNum>
                        {isTesla && rollout_status && <StatusTag status={rollout_status}>{statusLabel(rollout_status)}</StatusTag>}
                    </VersionCol>
                    {isTesla && <FsdCell>{(fsd_versions || []).join(', ') || '-'}</FsdCell>}
                    <Age>{relativeAge(first_detected_at)}</Age>
                    <Pct>{formatPct(percent_of_fleet)}</Pct>
                    <Arrow>&rsaquo;</Arrow>
                </Row>
            ))}
        </Wrapper>
    )
}
